import type {Module} from "vuex";
import type {RootState} from "@/store/types";
import {order} from "@/api/order";
import {retryRequest} from "@/helpers/request";

export interface Address {
    _id: string
    country: string
    [key: string]: unknown
}

// Dynamic address form field (see docs/*.json for the address_config schema)
export interface AddressConfigField {
    field: string
    type: string
    display?: {
        label?: string
        placeholder?: string
    }
    validation?: Record<string, unknown>
    [key: string]: unknown
}

export interface AddressConfigResponse {
    config: AddressConfigField[]
    header: unknown
    denyAddressCreation: boolean
}

export interface AddressRefreshOptions {
    start?: boolean
    interval?: number
}

export interface AddressState {
    config: AddressConfigField[] | null
    header: unknown
    denyAddressCreation: boolean | null
    items: Address[]
    all: Address[]
}

const filterAddresses = (addresses: Address[], country: string): Address[] => addresses.filter(address => address.country == country);

let REFRESH_INTERVAL: ReturnType<typeof setInterval> | undefined;

const CONFIG_FIELD_PERSONAL_EMAIL: AddressConfigField = {
    field: "personalEmail",
    type: "text",
    display: {
        label: "Personal Email",
        placeholder: "Personal Email"
    },
    validation: {
        required: true,
        email: true
    }
};
const CONFIG_FIELD_MANAGER_EMAIL: AddressConfigField = {
    field: "managerEmail",
    type: "text",
    display: {
        label: "Manager Email",
        placeholder: "Manager Email"
    },
    validation: {
        required: true,
        email: true
    }
};
const CONFIG_FIELD_MANAGER_NAME: AddressConfigField = {
    field: "managerName",
    type: "text",
    display: {
        label: "Manager's Name",
        placeholder: "Manager's Name"
    },
    validation: {
        required: true
    }
};

const address: Module<AddressState, RootState> = {
    namespaced: true,
    state: {
        config: null,
        header: null,
        denyAddressCreation: null,
        items: [],
        all: []
    },
    getters: {
        items: (state): Address[] => state.items,
        all: (state): Address[] => state.all,
        findItem: state => (id: Address["_id"]): Address | undefined => state.items.find(address => address._id == id),
        config: (state): AddressConfigField[] | null => state.config,
        header: (state): unknown => state.header,
        denyAddressCreation: (state): boolean | null => state.denyAddressCreation,
        configReturn: (state): (AddressConfigField | null)[] => [CONFIG_FIELD_PERSONAL_EMAIL, CONFIG_FIELD_MANAGER_NAME, CONFIG_FIELD_MANAGER_EMAIL]
            .concat(state.config as AddressConfigField[])
    },
    mutations: {
        setItems: (state, items: Address[]) => state.items = items,
        setAll: (state, all: Address[]) => state.all = all,
        addItem: (state, item: Address) => state.items.push(item),
        addAll: (state, item: Address) => state.all.push(item),
        updateItem: (state, item: Address) => state.items[state.items.findIndex(existing => existing._id == item._id)] = item,
        setConfig: (state, config: AddressConfigField[] | null) => state.config = config,
        setHeader: (state, header: unknown) => state.header = header,
        setDenyAddressCreation: (state, denyAddressCreation: boolean | null) => state.denyAddressCreation = denyAddressCreation
    },
    actions: {
        fetch(context) {

            const userId = this.getters["user/id"];
            const country = this.getters["user/profile/country"];

            return Promise.all([
                retryRequest(() => order.get<{results: Address[]}>(`/users/${userId}/addresses`), undefined, undefined, `get:/users/${userId}/addresses`),
                retryRequest(() => order.get<{results: AddressConfigResponse}>(`/users/${userId}/addresses/config`), undefined, undefined, `get:/users/${userId}/addresses/config`)
            ]).then(responses => {

                const [addressesResponse, configResponse] = responses;

                context.commit("setAll", addressesResponse.data.results);
                context.commit("setItems", filterAddresses(addressesResponse.data.results, country));
                context.commit("setConfig", configResponse.data.results.config);
                context.commit("setHeader", configResponse.data.results.header);
                context.commit("setDenyAddressCreation", configResponse.data.results.denyAddressCreation);
            });
        },
        post(context, model: Partial<Address>): Promise<Address> {

            const userId = this.getters["user/id"];

            return retryRequest(() => order.post<{results: Address}>(`/users/${userId}/addresses`, model))
                .then(response => {

                    const address = response.data.results;
                    context.commit("addItem", address);
                    context.commit("addAll", address);

                    return address;
                });
        },
        put(context, model: Address): Promise<Address> {

            const userId = this.getters["user/id"];

            return retryRequest(() => order.put<{results: Address}>(`/users/${userId}/addresses/${model._id}`, model))
                .then(response => context.dispatch("fetch").then(() => response.data.results));
        },
        clear(context) {

            context.commit("setItems", []);
        },
        confirm(context, addressId: Address["_id"]) {

            const userId = this.getters["user/id"];

            return retryRequest(() => order.post(`/users/${userId}/addresses/${addressId}/verification/send`));
        },
        delete(context, addressId: Address["_id"]) {

            const userId = this.getters["user/id"];

            return order
                .delete(`/users/${userId}/addresses/${addressId}`)
                .then(() => context.dispatch("fetch"));
        },
        // Starts (start + interval) or stops re-fetching the addresses in the background
        refresh(context, options: AddressRefreshOptions): boolean {

            if (options.start && options.interval) {

                const userId = this.getters["user/id"];
                const country = this.getters["user/profile/country"];
                const callback = () => retryRequest(() => order.get<{results: Address[]}>(`/users/${userId}/addresses`))
                    .then(response => context.commit("setItems", filterAddresses(response.data.results, country)));

                callback();

                clearInterval(REFRESH_INTERVAL);
                REFRESH_INTERVAL = setInterval(callback, options.interval);

                return true;
            }

            clearInterval(REFRESH_INTERVAL);

            return false;
        }
    },
    modules: {},
}

export default address
